import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, Shield, KeyRound, Database, FileCheck2, AlertTriangle, BadgeCheck, Server, Mail } from "lucide-react";

/**
 * Pannello "Postura di Sicurezza".
 * Usato come pagina interna del SuperAdmin (vedi /superadmin → Impostazioni → Audit Sicurezza).
 *
 * Props:
 *  - embedded (bool): se true, omette hero/CTA/footer e mostra solo il contenuto centrale.
 */
export default function Sicurezza({ embedded = false }) {
  useEffect(() => {
    if (!embedded) {
      window.scrollTo(0, 0);
      document.title = "Sicurezza & Privacy — Yollo";
    }
  }, [embedded]);

  const blocks = [
    {
      icon: KeyRound,
      title: "Password protette con bcrypt",
      body: "Le password dei tuoi account non vengono mai salvate in chiaro. Usiamo bcrypt con salt univoco per ciascun utente: anche in caso di compromissione del database, le password rimangono illeggibili. Nessuno — nemmeno il nostro staff — può vedere la tua password."
    },
    {
      icon: Lock,
      title: "Token di sessione robusti (JWT)",
      body: "L'accesso alla piattaforma è protetto da token JWT firmati con una chiave segreta a 512 bit, ruotata periodicamente. I token scadono automaticamente e ogni richiesta sensibile viene validata lato server."
    },
    {
      icon: Database,
      title: "Multi-tenancy isolata",
      body: "Ogni società ha un identificativo (societa_id) verificato su tutte le risorse: locali, lotti, frigoriferi, turni, buste paga, fatture. Un'altra società non può mai accedere ai tuoi dati, neppure conoscendo l'URL."
    },
    {
      icon: FileCheck2,
      title: "File e documenti protetti",
      body: "Buste paga, documenti aziendali e foto sono serviti solo dietro autenticazione. Includiamo controlli di tipo file (MIME), limite di dimensione e protezione contro path traversal. Niente file scaricabili pubblicamente con un URL pescato a caso."
    },
    {
      icon: AlertTriangle,
      title: "Rate limiting anti-brute-force",
      body: "Login, recupero password e altre operazioni sensibili sono protette da limiti di velocità: dopo pochi tentativi falliti l'IP viene temporaneamente bloccato. Difesa attiva contro attacchi automatizzati di credential stuffing."
    },
    {
      icon: Shield,
      title: "2FA obbligatoria per il personale Yollo",
      body: "L'accesso alla console di amministrazione interna (SuperAdmin) è protetto da autenticazione a due fattori (TOTP) con codici di recovery. Solo persone autorizzate, solo da dispositivi verificati."
    },
    {
      icon: Server,
      title: "Pagamenti gestiti da Stripe",
      body: "Yollo non vede mai i numeri delle carte di credito. Il flusso di pagamento avviene direttamente con Stripe, certificato PCI-DSS Level 1, lo standard più alto del settore. Salviamo solo un riferimento crittografato per il rinnovo dell'abbonamento."
    },
    {
      icon: Mail,
      title: "Email transazionali tracciate",
      body: "Tutte le email inviate (welcome, fatture, reset password) sono inviate via Resend con tracciamento di delivery, anti-spoofing DKIM/SPF e fallback SMTP redondante."
    },
    {
      icon: BadgeCheck,
      title: "Conformità GDPR (Reg. UE 2016/679)",
      body: "Diritto di accesso, rettifica, cancellazione, portabilità dei dati: tutti rispettati e disponibili su richiesta scritta. Cookie con Consent Mode v2 di Google, retention dei log limitata, no terze parti pubblicitarie."
    },
  ];

  return (
    <div className={embedded ? "text-gray-800" : "min-h-screen bg-white text-gray-800"} data-testid="sicurezza-page">

      {/* Hero — solo in modalità pubblica */}
      {!embedded && (
      <div data-on-dark="true" className="bg-gradient-to-br from-[#1F1B3D] via-[#3A2F8E] to-[#6C63FF] text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3) 0px, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0px, transparent 50%)" }}></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <Link to="/" className="text-white hover:text-white text-sm mb-6 inline-block opacity-90 hover:opacity-100" data-testid="back-to-yollo">← Torna a Yollo</Link>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full px-4 py-1.5 text-xs font-semibold mb-6 text-white">
            <Shield className="w-3.5 h-3.5" /> Audit di sicurezza completato — Maggio 2026
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white">
            I tuoi dati,<br/>
            <span className="text-white">trattati come fossero i nostri.</span>
          </h1>
          <p className="mt-6 text-white text-lg max-w-2xl leading-relaxed">
            Yollo gestisce informazioni sensibili: buste paga, contratti, fornitori, fatture.
            Abbiamo costruito la piattaforma con la sicurezza come prerequisito, non come opzione.
            Ecco esattamente cosa facciamo per proteggerti.
          </p>
        </div>
      </div>
      )}

      {/* Header compatto in modalità embedded */}
      {embedded && (
        <div className="mb-6">
          <h2 className="text-2xl font-black tracking-tight text-slate-900">Postura di sicurezza Yollo</h2>
          <p className="text-sm text-gray-600 mt-1">Ultimo audit completato — Maggio 2026 · Pannello interno SuperAdmin</p>
        </div>
      )}

      {/* Stats / KPI */}
      <div className={embedded ? "bg-white border border-gray-200 rounded-2xl py-6 px-4 mb-6" : "bg-[#F5F4FE] border-b border-gray-200 py-10 px-6"}>
        <div className={embedded ? "grid grid-cols-2 sm:grid-cols-4 gap-4 text-center" : "max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center"}>
          <div>
            <div className="text-3xl font-bold text-[#6C63FF]">bcrypt</div>
            <div className="text-xs text-gray-600 mt-1 uppercase tracking-wide">Hashing password</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-[#6C63FF]">512-bit</div>
            <div className="text-xs text-gray-600 mt-1 uppercase tracking-wide">Chiavi JWT</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-[#6C63FF]">PCI-DSS</div>
            <div className="text-xs text-gray-600 mt-1 uppercase tracking-wide">Pagamenti via Stripe</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-[#6C63FF]">GDPR</div>
            <div className="text-xs text-gray-600 mt-1 uppercase tracking-wide">UE 2016/679</div>
          </div>
        </div>
      </div>

      {/* Sezioni */}
      <div className={embedded ? "mb-6" : "max-w-5xl mx-auto px-6 py-16"}>
        <h2 className={embedded ? "text-xl font-bold text-gray-900 mb-2" : "text-2xl sm:text-3xl font-bold text-gray-900 mb-3"}>
          Le 9 difese che girano in background
        </h2>
        {!embedded && <p className="text-gray-600 mb-12 max-w-2xl">Non te ne accorgerai mai, ma sono attive 24/7 su ogni clic che fai.</p>}

        <div className={embedded ? "grid sm:grid-cols-2 gap-x-6 gap-y-6 mt-4" : "grid sm:grid-cols-2 gap-x-8 gap-y-10"}>
          {blocks.map(({ icon: Icon, title, body }, i) => (
            <div key={i} className="flex gap-4" data-testid={`security-block-${i}`}>
              <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-[#F5F4FE] border border-[#6C63FF]/20 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#6C63FF]" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-base">{title}</h3>
                <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Audit timeline */}
      <div className={embedded ? "bg-white border border-gray-200 rounded-2xl p-6 mb-6" : "bg-gray-50 border-y border-gray-200 py-16 px-6"}>
        <div className={embedded ? "" : "max-w-4xl mx-auto"}>
          <h2 className={embedded ? "text-lg font-bold text-gray-900 mb-2" : "text-2xl sm:text-3xl font-bold text-gray-900 mb-3"}>L'ultimo audit di sicurezza</h2>
          <p className="text-gray-600 mb-6 text-sm">Risultati dell'audit di Maggio 2026:</p>

          <div className="space-y-3">
            <AuditRow num="9" label="vulnerabilità critiche identificate" color="text-red-500" />
            <AuditRow num="9" label="risolte e verificate (100%)" color="text-emerald-500" />
            <AuditRow num="23 / 23" label="test di sicurezza automatizzati superati" color="text-emerald-500" />
            <AuditRow num="0" label="dati esposti in chiaro nel nostro database" color="text-emerald-500" />
          </div>

          {!embedded && (
            <div className="mt-10 p-6 bg-white border border-gray-200 rounded-2xl">
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Trasparenza:</strong> in caso di incidente di sicurezza che coinvolga i tuoi dati,
                ti notificheremo entro <strong>72 ore</strong> come previsto dall'articolo 33 del GDPR, fornendoti tutti i dettagli
                dell'incidente, le misure adottate e le raccomandazioni per proteggerti.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Cosa puoi fare tu — solo modalità pubblica */}
      {!embedded && (
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Cosa puoi fare anche tu</h2>
        <p className="text-gray-600 mb-8">La sicurezza è una squadra. Ecco come dare una mano:</p>

        <ul className="space-y-4">
          <Tip text="Usa una password lunga e non banale (almeno 12 caratteri, niente '123456' o 'azienda2024')." />
          <Tip text="Non condividere le credenziali tra colleghi: ogni operatore deve avere il proprio account." />
          <Tip text="Quando un dipendente esce, disattivalo subito dalla pagina Anagrafica → Utenti." />
          <Tip text="Se ricevi una mail sospetta che chiede la tua password, ignorala: noi non chiediamo mai password via email." />
          <Tip text="Se trovi una vulnerabilità, scrivici a security@yollo.me — apprezziamo le segnalazioni responsabili." />
        </ul>
      </div>
      )}

      {/* CTA finale — solo modalità pubblica */}
      {!embedded && (
      <div data-on-dark="true" className="bg-[#1F1B3D] text-white py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">Domande sulla sicurezza?</h2>
          <p className="text-white mb-8 max-w-xl mx-auto opacity-95">
            Vuoi vedere il certificato di conformità, leggere il nostro DPA o discutere requisiti speciali del tuo settore?
            Siamo a disposizione.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="mailto:security@yollo.me"
              className="inline-flex items-center gap-2 bg-white text-[#1F1B3D] px-6 py-3 rounded-full font-semibold hover:bg-[#F5F4FE] hover:text-[#1F1B3D] transition shadow-lg"
              data-testid="cta-contact-security"
            >
              <Mail className="w-4 h-4" /> security@yollo.me
            </a>
            <Link
              to="/privacy"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[#1F1B3D] transition"
              data-testid="cta-privacy-policy"
            >
              Leggi la Privacy Policy
            </Link>
          </div>
        </div>
      </div>
      )}

      {/* Footer minimo — solo modalità pubblica */}
      {!embedded && (
        <div className="text-center py-8 text-sm text-gray-500">
          © {new Date().getFullYear()} Yollo — Luchapp S.r.l. ·{" "}
          <Link to="/privacy" className="text-[#6C63FF] hover:underline">Privacy</Link> ·{" "}
          <Link to="/cookie-policy" className="text-[#6C63FF] hover:underline">Cookie</Link>
        </div>
      )}
    </div>
  );
}

function AuditRow({ num, label, color }) {
  return (
    <div className="flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl">
      <div className={`text-3xl font-bold ${color} min-w-[60px] text-center`}>{num}</div>
      <div className="text-sm text-gray-700">{label}</div>
    </div>
  );
}

function Tip({ text }) {
  return (
    <li className="flex gap-3 text-gray-700 text-[15px]">
      <BadgeCheck className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
      <span>{text}</span>
    </li>
  );
}
